import { useState, useRef, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useTranslation } from '@/hooks/useTranslation';
import type { EntityOption } from '@/hooks/useEntityOptions';

interface EntityCellEditorProps {
  value: unknown;
  options: EntityOption[];
  allowCreate?: boolean;
  /** Label for the create item; defaults to `Create "{name}"`. */
  createLabel?: (name: string) => string;
  /** Heading per option value — groups the list (e.g. Businesses / People). */
  optionGroup?: (value: string) => string | undefined;
  ariaLabel?: string;
  onChange: (value: string | null) => void;
  onCommit: () => void;
  onCancel: () => void;
}

export function EntityCellEditor({ value, options, allowCreate, createLabel, optionGroup, ariaLabel, onChange, onCommit, onCancel }: EntityCellEditorProps) {
  const { t } = useTranslation('spreadsheet');
  const [open, setOpen] = useState(true);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Find current label
  const currentLabel = options.find(o => o.value === String(value ?? ''))?.label
    ?? (typeof value === 'string' && value.startsWith('_create:') ? value.slice(8) : '');

  // Check if search term matches an existing option exactly
  const hasExactMatch = useMemo(() => {
    if (!search.trim()) return true;
    return options.some(o => o.label.toLowerCase() === search.trim().toLowerCase());
  }, [search, options]);

  // Options bucketed by heading, in first-seen order; ungrouped ones first.
  const groups = useMemo(() => {
    const ungrouped: EntityOption[] = [];
    const headed = new Map<string, EntityOption[]>();
    for (const option of options) {
      const heading = optionGroup?.(option.value);
      if (!heading) {
        ungrouped.push(option);
        continue;
      }
      const bucket = headed.get(heading);
      if (bucket) bucket.push(option);
      else headed.set(heading, [option]);
    }
    return { ungrouped, headed: [...headed] };
  }, [options, optionGroup]);

  const renderOption = (option: EntityOption) => (
    <CommandItem
      key={option.value}
      value={option.label}
      onSelect={() => handleSelect(option.value)}
    >
      {option.label}
    </CommandItem>
  );

  const createItemLabel = (name: string) => createLabel?.(name) ?? t('cells.create', { name });

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue || null);
    setOpen(false);
    onCommit();
  };

  const handleCreate = () => {
    const name = search.trim();
    if (!name) return;
    onChange(`_create:${name}`);
    setOpen(false);
    onCommit();
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      onCommit();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          className="cell-editing-input text-xs text-left"
          aria-label={ariaLabel}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              onCancel();
            }
          }}
        >
          {currentLabel || <span className="text-muted-foreground">{t('cells.select')}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0" align="start" sideOffset={2}>
        <Command>
          <CommandInput
            placeholder={t('cells.search')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {allowCreate && search.trim() ? (
                <button
                  type="button"
                  className="flex items-center gap-1.5 w-full px-2 py-1.5 text-sm hover:bg-accent rounded cursor-pointer"
                  onClick={handleCreate}
                >
                  <Plus className="h-3.5 w-3.5" />
                  {createItemLabel(search.trim())}
                </button>
              ) : (
                t('cells.noResults')
              )}
            </CommandEmpty>
            <CommandGroup>
              {/* Clear option */}
              <CommandItem
                value=" "
                onSelect={() => handleSelect('')}
                className="text-muted-foreground italic"
              >
                {t('cells.clear')}
              </CommandItem>
              {groups.ungrouped.map(renderOption)}
            </CommandGroup>
            {groups.headed.map(([heading, groupOptions]) => (
              <CommandGroup key={heading} heading={heading}>
                {groupOptions.map(renderOption)}
              </CommandGroup>
            ))}
            {/* Create option when search doesn't match existing — last, so a
                real match stays the highlighted (Enter) item. */}
            {allowCreate && search.trim() && !hasExactMatch && (
              <CommandGroup>
                <CommandItem
                  value={`__create__${search.trim()}`}
                  onSelect={handleCreate}
                  className="text-primary"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  {createItemLabel(search.trim())}
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}